import { useEffect, useMemo, useRef, useState } from 'react'
import QuestionForm from '../components/QuestionForm'
import { api } from '../lib/api'

const box = { background: '#161d31', borderRadius: 12, padding: 16, marginBottom: 16 }
const input = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: 'white' }
const label = { display: 'block', fontSize: 14, color: '#94a3b8', marginBottom: 6 }
const btn = { background: '#4f46e5', color: 'white', border: 0, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }
const subtleBtn = { ...btn, background: '#334155' }
const ghostBtn = { ...btn, background: '#1e293b', color: '#cbd5e1' }

const statusTone = {
  imported: { bg: '#172554', fg: '#93c5fd' },
  needs_review: { bg: '#3f2c06', fg: '#fcd34d' },
  approved: { bg: '#052e16', fg: '#86efac' },
  archived: { bg: '#3f3f46', fg: '#d4d4d8' },
}

const emptyForm = {
  admin_title: '',
  stem: '',
  rationale_brief: '',
  rationale_full: '',
  distractor_rationale: '',
  status: 'needs_review',
  confidence: '',
  difficulty: '',
  cognitive_level: '',
  notes: '',
  primary_category_id: '',
  vtne_domain_id: '',
  choices: [
    { label: 'A', choice_text: '', is_correct: true, sort_order: 0 },
    { label: 'B', choice_text: '', is_correct: false, sort_order: 1 },
    { label: 'C', choice_text: '', is_correct: false, sort_order: 2 },
    { label: 'D', choice_text: '', is_correct: false, sort_order: 3 },
  ],
}

export default function QuestionBankPage() {
  const [questions, setQuestions] = useState([])
  const [categories, setCategories] = useState([])
  const [domains, setDomains] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [selectedId, setSelectedId] = useState(null)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [filters, setFilters] = useState({ q: '', category_id: '', vtne_domain_id: '', status: '', sort: 'updated_desc' })
  const [queueSearch, setQueueSearch] = useState('')
  const [workMode, setWorkMode] = useState('all')
  const [lastSelectedByMode, setLastSelectedByMode] = useState({})
  const [form, setForm] = useState({})
  const [createForm, setCreateForm] = useState(emptyForm)
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkCategoryId, setBulkCategoryId] = useState('')
  const [bulkVtneDomainId, setBulkVtneDomainId] = useState('')
  const [bulkStatus, setBulkStatus] = useState('')
  const [bulkWorking, setBulkWorking] = useState(false)
  const previousWorkModeRef = useRef(workMode)

  async function loadLists() {
    try {
      const [cats, doms] = await Promise.all([api.categories(), api.vtneDomains()])
      setCategories(cats)
      setDomains(doms)
    } catch (err) {
      setError('Failed to load data: ' + err.message)
    }
  }

  async function loadQuestions() {
    setLoading(true)
    try {
      const rows = await api.questions(filters)
      setQuestions(rows)
      if (rows.length && !selectedId) setSelectedId(rows[0].id)
      return rows
    } finally {
      setLoading(false)
    }
  }

  async function loadDetail(id) {
    if (!id) return
    const item = await api.question(id)
    setDetail(item)
    setForm({
      admin_title: item.admin_title || '',
      stem: item.stem || '',
      rationale_brief: item.rationale_brief || '',
      rationale_full: item.rationale_full || '',
      distractor_rationale: item.distractor_rationale || '',
      status: item.status || '',
      confidence: item.confidence || '',
      difficulty: item.difficulty || '',
      cognitive_level: item.cognitive_level || '',
      notes: item.notes || '',
      primary_category_id: item.primary_category_id || '',
      vtne_domain_id: item.vtne_domain_id || '',
      choices: (item.choices || []).map(c => ({
        label: c.label,
        choice_text: c.choice_text,
        is_correct: c.is_correct,
        sort_order: c.sort_order,
      })),
    })
  }

  useEffect(() => { loadLists() }, [])
  useEffect(() => { loadQuestions() }, [filters])
  useEffect(() => { if (selectedId) loadDetail(selectedId) }, [selectedId])

  const modeFilteredQuestions = useMemo(() => {
    switch (workMode) {
      case 'needs_review':
        return questions.filter(q => q.status === 'needs_review')
      case 'imported':
        return questions.filter(q => q.status === 'imported')
      case 'approved':
        return questions.filter(q => q.status === 'approved')
      case 'archived':
        return questions.filter(q => q.status === 'archived')
      default:
        return questions
    }
  }, [questions, workMode])

  const visibleQuestions = useMemo(() => {
    const q = queueSearch.trim().toLowerCase()
    if (!q) return modeFilteredQuestions
    return modeFilteredQuestions.filter(question => {
      return `${question.admin_title || ''} ${question.category_name || ''} ${question.vtne_domain_name || ''} ${question.status || ''}`.toLowerCase().includes(q)
    })
  }, [modeFilteredQuestions, queueSearch])

  useEffect(() => {
    if (selectedId) {
      setLastSelectedByMode(current => current[workMode] === selectedId ? current : { ...current, [workMode]: selectedId })
    }
  }, [selectedId, workMode])

  useEffect(() => {
    const workModeChanged = previousWorkModeRef.current !== workMode
    previousWorkModeRef.current = workMode

    if (visibleQuestions.length === 0) {
      setSelectedId(null)
      setDetail(null)
      return
    }

    const selectedStillVisible = visibleQuestions.some(question => question.id === selectedId)

    if (workModeChanged) {
      const preferredId = lastSelectedByMode[workMode]
      if (preferredId && visibleQuestions.some(question => question.id === preferredId) && selectedId !== preferredId) {
        setSelectedId(preferredId)
        return
      }
    }

    if (!selectedStillVisible) {
      setSelectedId(visibleQuestions[0].id)
    }
  }, [visibleQuestions, selectedId, workMode, lastSelectedByMode])

  async function applyFilters(e) {
    e.preventDefault()
    await loadQuestions()
  }

  function getNextVisibleQuestionId(currentId) {
    const questionIndex = visibleQuestions.findIndex(q => q.id === currentId)
    if (questionIndex === -1) return visibleQuestions[0]?.id || null
    return visibleQuestions[questionIndex + 1]?.id || visibleQuestions[questionIndex - 1]?.id || null
  }

  function jumpToFirstVisibleQuestion() {
    if (visibleQuestions.length > 0) {
      setSelectedId(visibleQuestions[0].id)
    }
  }

  function syncSelectionAfterRefresh(refreshedQuestions, preferredId = null) {
    const filteredRefreshed = refreshedQuestions.filter(q => {
      if (workMode === 'all') return true
      return q.status === workMode
    })
    if (preferredId && filteredRefreshed.some(question => question.id === preferredId)) {
      setSelectedId(preferredId)
      return
    }
    setSelectedId(filteredRefreshed[0]?.id || refreshedQuestions[0]?.id || null)
  }

  async function saveQuestion(options = {}) {
    if (!selectedId) return
    const nextId = options.advance ? getNextVisibleQuestionId(selectedId) : null
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const payload = { ...form }
      if (options.statusOverride) payload.status = options.statusOverride
      if (payload.primary_category_id === '') payload.primary_category_id = null
      if (payload.vtne_domain_id === '') payload.vtne_domain_id = null
      const updated = await api.updateQuestion(selectedId, payload)
      setDetail(updated)
      const refreshed = await loadQuestions()
      if (options.advance) syncSelectionAfterRefresh(refreshed, nextId)
      if (options.statusOverride && options.advance) {
        setMessage(`✓ Saved as ${options.statusOverride} and moved to next question`)
      } else if (options.statusOverride) {
        setMessage(`✓ Saved as ${options.statusOverride}`)
      } else {
        setMessage(options.advance ? '✓ Saved and moved to next question' : '✓ Saved question')
      }
    } catch (err) {
      setError(String(err))
    } finally {
      setSaving(false)
    }
  }

  async function createQuestion() {
    setCreating(true)
    setError('')
    setMessage('')
    try {
      const payload = { ...createForm }
      if (payload.primary_category_id === '') payload.primary_category_id = null
      if (payload.vtne_domain_id === '') payload.vtne_domain_id = null
      const created = await api.createQuestion(payload)
      setCreateForm(emptyForm)
      setShowCreate(false)
      await loadQuestions()
      setSelectedId(created.id)
      setMessage('✓ Created question')
    } catch (err) {
      setError(String(err))
    } finally {
      setCreating(false)
    }
  }

  function toggleSelected(id) {
    setSelectedIds(current => current.includes(id) ? current.filter(v => v !== id) : [...current, id])
  }

  function toggleSelectAll() {
    if (visibleQuestions.length > 0 && visibleQuestions.length === selectedIds.length && visibleQuestions.every(q => selectedIds.includes(q.id))) {
      setSelectedIds([])
    } else {
      setSelectedIds(visibleQuestions.map(q => q.id))
    }
  }

  async function bulkAction(action, payload = {}) {
    if (selectedIds.length === 0) return
    setBulkWorking(true)
    setMessage('')
    setError('')
    try {
      const result = await api.bulkQuestions({ action, question_ids: selectedIds, ...payload })
      await loadQuestions()
      setSelectedIds([])
      setMessage(`✓ ${action.replace(/_/g, ' ')}: updated ${result.updated_count} question(s)`)
    } catch (err) {
      setError(String(err))
    } finally {
      setBulkWorking(false)
    }
  }

  const allSelected = visibleQuestions.length > 0 && visibleQuestions.every(q => selectedIds.includes(q.id))
  const selectedQuestion = visibleQuestions.find(q => q.id === selectedId)
  const selectedIndex = visibleQuestions.findIndex(q => q.id === selectedId)
  const statusCounts = useMemo(() => ({
    all: questions.length,
    imported: questions.filter(q => q.status === 'imported').length,
    needs_review: questions.filter(q => q.status === 'needs_review').length,
    approved: questions.filter(q => q.status === 'approved').length,
    archived: questions.filter(q => q.status === 'archived').length,
  }), [questions])

  useEffect(() => {
    function handleKeyDown(e) {
      const tag = e.target?.tagName
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
      if (isTyping || visibleQuestions.length === 0) return

      const currentIndex = visibleQuestions.findIndex(q => q.id === selectedId)
      if (currentIndex === -1) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          if (currentIndex < visibleQuestions.length - 1) setSelectedId(visibleQuestions[currentIndex + 1].id)
          break
        case 'ArrowUp':
          e.preventDefault()
          if (currentIndex > 0) setSelectedId(visibleQuestions[currentIndex - 1].id)
          break
        case 'a':
        case 'A':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          saveQuestion({ statusOverride: 'approved', advance: true })
          break
        case 'n':
        case 'N':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          saveQuestion({ statusOverride: 'needs_review', advance: true })
          break
        case 's':
        case 'S':
          e.preventDefault()
          if (e.ctrlKey || e.metaKey) return
          saveQuestion({ advance: true })
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [visibleQuestions, selectedId, form])

  return (
    <div>
      {error && <div style={{ color: '#fca5a5', background: '#450a0a', padding: 16, borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      {message && <div style={{ color: '#86efac', background: '#052e16', padding: 16, borderRadius: 8, marginBottom: 16 }}>{message}</div>}

      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div>
            <h2 style={{ marginTop: 0, marginBottom: 6 }}>Question Bank</h2>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Desktop-first review workspace for the canonical question bank.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button style={subtleBtn} onClick={() => setShowCreate(v => !v)}>
              {showCreate ? 'Hide Create Form' : 'New Question'}
            </button>
            <button style={ghostBtn} onClick={jumpToFirstVisibleQuestion} disabled={visibleQuestions.length === 0}>Jump to First</button>
            <button
              style={ghostBtn}
              onClick={() => {
                const remembered = lastSelectedByMode[workMode]
                if (remembered && visibleQuestions.some(question => question.id === remembered)) setSelectedId(remembered)
              }}
              disabled={!lastSelectedByMode[workMode] || !visibleQuestions.some(question => question.id === lastSelectedByMode[workMode])}
            >
              Continue Where I Left Off
            </button>
          </div>
        </div>
      </div>

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.72)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{ width: 'min(760px, 96vw)', height: '100vh', overflowY: 'auto', background: '#0b1120', borderLeft: '1px solid #334155', padding: 24, boxShadow: '-12px 0 32px rgba(0,0,0,0.35)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0 }}>Create Question</h3>
                <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Slide-over composer for creating canonical questions without disrupting queue review.</div>
              </div>
              <button style={ghostBtn} onClick={() => setShowCreate(false)}>Close</button>
            </div>
            <QuestionForm form={createForm} setForm={setCreateForm} categories={categories} domains={domains} showChoices={true} />
            <div style={{ height: 16 }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button style={btn} onClick={createQuestion} disabled={creating}>{creating ? 'Creating…' : 'Create Question'}</button>
              <button style={subtleBtn} onClick={() => setShowCreate(false)} disabled={creating}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: workMode === 'all' ? '460px minmax(0, 1fr)' : '400px minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
        <div>
          <div style={box}>
            <form onSubmit={applyFilters}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) repeat(4, minmax(0,1fr)) auto', gap: 8, alignItems: 'end' }}>
                <div>
                  <label style={{ ...label, marginBottom: 4 }}>Search API query</label>
                  <input style={{ ...input, padding: 9 }} value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} />
                </div>
                <div>
                  <label style={{ ...label, marginBottom: 4 }}>Category</label>
                  <select style={{ ...input, padding: 9 }} value={filters.category_id} onChange={e => setFilters({ ...filters, category_id: e.target.value })}>
                    <option value="">All</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ ...label, marginBottom: 4 }}>VTNE domain</label>
                  <select style={{ ...input, padding: 9 }} value={filters.vtne_domain_id} onChange={e => setFilters({ ...filters, vtne_domain_id: e.target.value })}>
                    <option value="">All</option>
                    {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ ...label, marginBottom: 4 }}>Status</label>
                  <select style={{ ...input, padding: 9 }} value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                    <option value="">All</option>
                    <option value="imported">imported</option>
                    <option value="needs_review">needs_review</option>
                    <option value="approved">approved</option>
                    <option value="archived">archived</option>
                  </select>
                </div>
                <div>
                  <label style={{ ...label, marginBottom: 4 }}>Sort</label>
                  <select style={{ ...input, padding: 9 }} value={filters.sort} onChange={e => setFilters({ ...filters, sort: e.target.value })}>
                    <option value="updated_desc">Updated desc</option>
                    <option value="title_asc">Title asc</option>
                    <option value="title_desc">Title desc</option>
                    <option value="vtne_domain_asc">VTNE domain</option>
                  </select>
                </div>
                <button style={{ ...btn, padding: '10px 12px', whiteSpace: 'nowrap' }} type="submit">Apply</button>
              </div>
            </form>
          </div>

          <div style={box}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Question Queue ({visibleQuestions.length})</h3>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>{questions.length} total loaded</div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {[
                ['all', `All ${statusCounts.all}`],
                ['needs_review', `Needs Review ${statusCounts.needs_review}`],
                ['imported', `Imported ${statusCounts.imported}`],
                ['approved', `Approved ${statusCounts.approved}`],
                ['archived', `Archived ${statusCounts.archived}`],
              ].map(([mode, label]) => {
                const active = workMode === mode
                return (
                  <button
                    key={mode}
                    onClick={() => setWorkMode(mode)}
                    style={{
                      ...ghostBtn,
                      background: active ? '#4f46e5' : '#1e293b',
                      color: 'white',
                      padding: '8px 10px',
                      fontSize: 12,
                    }}
                  >
                    {label}
                  </button>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <button style={subtleBtn} onClick={toggleSelectAll} disabled={visibleQuestions.length === 0}>
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <input
              style={{ ...input, marginBottom: 12 }}
              value={queueSearch}
              onChange={e => setQueueSearch(e.target.value)}
              placeholder="Search loaded questions…"
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              <span>Mode: {workMode.replace(/_/g, ' ')}</span>
              <span>{visibleQuestions.length} visible</span>
            </div>

            {selectedIds.length > 0 && (
              <div style={{ marginBottom: 12, padding: 10, background: '#1e3a8a', borderRadius: 8, color: '#93c5fd', fontSize: 14 }}>
                {selectedIds.length} selected
              </div>
            )}

            {selectedIds.length > 0 && (
              <div style={{ marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select style={input} value={bulkCategoryId} onChange={e => setBulkCategoryId(e.target.value)}>
                  <option value="">Assign Category…</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select style={input} value={bulkVtneDomainId} onChange={e => setBulkVtneDomainId(e.target.value)}>
                  <option value="">Assign Domain…</option>
                  {domains.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <select style={{ ...input, gridColumn: '1 / span 2' }} value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
                  <option value="">Change Status…</option>
                  <option value="needs_review">needs_review</option>
                  <option value="approved">approved</option>
                  <option value="archived">archived</option>
                </select>
                <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button style={btn} onClick={() => bulkAction('assign_category', { primary_category_id: Number(bulkCategoryId) })} disabled={bulkWorking || !bulkCategoryId}>Assign Category</button>
                  <button style={btn} onClick={() => bulkAction('assign_vtne_domain', { vtne_domain_id: Number(bulkVtneDomainId) })} disabled={bulkWorking || !bulkVtneDomainId}>Assign Domain</button>
                  <button style={btn} onClick={() => bulkAction('change_status', { status: bulkStatus })} disabled={bulkWorking || !bulkStatus}>Change Status</button>
                </div>
              </div>
            )}

            {loading ? <div>Loading…</div> : visibleQuestions.length > 0 ? (
              <div style={{ maxHeight: 'calc(100vh - 330px)', overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {visibleQuestions.map(q => {
                  const isSelected = selectedId === q.id
                  const tone = statusTone[q.status] || { bg: '#1e293b', fg: '#cbd5e1' }
                  return (
                    <button
                      key={q.id}
                      onClick={() => setSelectedId(q.id)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        background: isSelected ? '#273449' : '#0f172a',
                        border: isSelected ? '2px solid #4f46e5' : '1px solid #334155',
                        borderLeft: `6px solid ${isSelected ? '#818cf8' : tone.fg}`,
                        borderRadius: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(q.id)}
                          onChange={(e) => {
                            e.stopPropagation()
                            toggleSelected(q.id)
                          }}
                          style={{ cursor: 'pointer', marginTop: 2 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 12, color: '#f8fafc', marginBottom: 2 }}>{q.admin_title}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.25 }}>
                                {q.category_name || 'No category'} · {q.vtne_domain_name || 'No domain'}
                              </div>
                            </div>
                            <div style={{ ...tone, padding: '3px 7px', borderRadius: 999, fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              {q.status}
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : <div style={{ color: '#94a3b8' }}>No questions found</div>}
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ ...box, position: 'sticky', top: 16 }}>
            {!detail ? (
              <>
                <h2 style={{ marginTop: 0, marginBottom: 16 }}>Question Inspector</h2>
                <div style={{ color: '#94a3b8' }}>Select a question from the queue to review it here.</div>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>
                      {selectedIndex >= 0 ? `Question ${selectedIndex + 1} of ${visibleQuestions.length}` : 'Question Inspector'}
                    </div>
                    <h2 style={{ marginTop: 0, marginBottom: 4 }}>{detail.admin_title || 'Untitled'}</h2>
                    <div style={{ color: '#94a3b8', fontSize: 13 }}>
                      {(detail.category_name || 'No category')} • {(detail.vtne_domain_name || 'No domain')} • {detail.status}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <button style={btn} onClick={() => saveQuestion({ statusOverride: 'approved', advance: true })} disabled={saving}>{saving ? 'Saving…' : 'Approve + Next'}</button>
                    <button style={subtleBtn} onClick={() => saveQuestion({ statusOverride: 'needs_review', advance: true })} disabled={saving}>{saving ? 'Saving…' : 'Needs Review + Next'}</button>
                    <button style={ghostBtn} onClick={() => saveQuestion({ advance: true })} disabled={saving}>{saving ? 'Saving…' : 'Save + Next'}</button>
                    <button style={subtleBtn} onClick={() => saveQuestion()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                  </div>
                </div>

                <QuestionForm form={form} setForm={setForm} categories={categories} domains={domains} showChoices={true} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
